import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Film, Image, Play, Eye, Loader2, FolderOpen,
  ChevronRight, ArrowLeft, AlertTriangle, Check, Images, Wand2, Sparkles, X
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_CLASSES = {
  unprocessed: "status-unprocessed", processing: "status-scheduled",
  queued: "status-queued", posted: "status-published",
  archived: "status-rejected", generating: "status-scheduled",
};

const STATUS_LABELS = {
  unprocessed: "not yet posted", processing: "processing",
  queued: "queued", posted: "posted", archived: "archived",
  generating: "generating", published: "published", failed: "failed",
};

const KLING_MODELS = [
  { value: "kling-v1-6", label: "Kling v1.6 (latest)" },
  { value: "kling-v1-5", label: "Kling v1.5" },
  { value: "kling-v2", label: "Kling v2.0" },
  { value: "kling-v3", label: "Kling 3.0 (when available)" },
];

function ContentCard({ item, onClick }) {
  const mins = Math.floor((item.duration || 0) / 60);
  const secs = (item.duration || 0) % 60;
  const isPhoto = item.type === "carousel" || item.type === "photo";
  const isGenerating = item.status === "generating";
  const isAiGen = item.ai_generated || item.source === "kling";
  const isVideo = !isPhoto && !isGenerating;
  const statusLabel = STATUS_LABELS[item.status] || item.status;

  return (
    <div onClick={() => onClick?.(item)}
      className={`bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden brand-card group cursor-pointer ${isGenerating ? "animate-pulse-slow" : ""}`}
      data-testid={`content-card-${item.id}`}>
      <div className="relative aspect-video bg-studio-surface overflow-hidden">
        {isGenerating ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#1a1d21] to-[#21252b]">
            <Loader2 size={28} className="animate-spin text-gold" />
            <span className="text-[11px] text-gold font-mono">generating...</span>
          </div>
        ) : item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isPhoto ? <Images size={32} className="text-zinc-600" /> : <Film size={32} className="text-zinc-600" />}
          </div>
        )}
        {!isGenerating && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none">
            <div className="absolute bottom-3 left-3 flex items-center gap-2">
              {isPhoto ? (
                <span className="text-white text-xs font-mono">{item.photo_count || 0} photos</span>
              ) : (
                <span className="text-white text-xs font-mono">{mins}:{secs.toString().padStart(2, "0")}</span>
              )}
            </div>
          </div>
        )}
        {/* Centered play icon on hover */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
              <Play size={20} className="text-white ml-0.5" />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          {isAiGen && (
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#a78bfa20] text-[#a78bfa] border border-[#a78bfa30] flex items-center gap-1">
              <Sparkles size={8} />ai
            </span>
          )}
          <span className={`status-badge ${STATUS_CLASSES[item.status] || "status-unprocessed"}`}>{statusLabel}</span>
        </div>
        {item.source === "dropbox" && (
          <div className="absolute top-2 left-2">
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#0061ff20] text-[#5b9cff] border border-[#0061ff30]">dropbox</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-zinc-200 leading-snug line-clamp-2 mb-2">{item.title}</h3>
        {item.status === "failed" && item.error && (
          <p className="text-[11px] text-rose-300/90 leading-snug line-clamp-2 mb-2 font-mono"
            title={item.error}>
            {item.error}
          </p>
        )}
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: item.brand_color || "#71717a" }} />
            {item.brand_name || item.brand_id}
          </span>
          <span className="flex items-center gap-1 ml-auto">
            {item.source === "dropbox" ? (
              <><Images size={12} /><span className="font-mono">{item.photo_count || 0}</span></>
            ) : (
              <><Eye size={12} /><span className="font-mono">{(item.view_count || 0).toLocaleString()}</span></>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-600 mt-2">
          <span className="capitalize font-mono">{item.source}</span>
          <span>--</span>
          <span>{item.type}</span>
        </div>
      </div>
    </div>
  );
}


function DetailPanel({ item, brands, onClose, onCreatePost }) {
  // The list payload has no posting history / privacy — fetch the detail record.
  const [detail, setDetail] = useState(null);
  const [postingFull, setPostingFull] = useState(false);
  const [creatingCarousel, setCreatingCarousel] = useState(false);
  useEffect(() => {
    let alive = true;
    setDetail(null);
    if (item?.id) {
      api.get(`/content/${item.id}`).then((r) => { if (alive) setDetail(r.data); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [item?.id]);
  const posting = detail?.posting;
  const privacy = detail?.source_privacy || item?.source_privacy;
  const isPublic = !privacy || privacy === "anybody" || privacy === "public";
  if (!item) return null;
  const brand = brands.find((b) => b.id === item.brand_id);
  const isVimeo = item.source === "vimeo";
  const isYoutube = item.source === "youtube";
  const vimeoId = isVimeo ? item.source_id?.split("/").pop() : "";
  const youtubeId = isYoutube ? item.source_id : "";

  return (
    <div className="fixed top-[32px] right-[340px] w-[420px] h-[calc(100vh-32px)] bg-[#0f1114] border-l border-[#222630] z-[48] flex flex-col overflow-y-auto animate-slideIn"
      data-testid="detail-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#222630] flex-shrink-0">
        <span className="section-label">// detail</span>
        <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-[#21252b] transition-all" data-testid="detail-close">
          <X size={15} />
        </button>
      </div>
      <div className="aspect-video bg-studio-surface flex-shrink-0">
        {isVimeo && vimeoId && Number(vimeoId) > 200000 ? (
          <iframe src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
            className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Vimeo" />
        ) : isYoutube && youtubeId ? (
          <iframe src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1`}
            className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="YouTube" />
        ) : item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film size={32} className="text-zinc-600" /></div>
        )}
      </div>
      <div className="p-5 space-y-4 flex-1">
        <div>
          <h3 className="text-base font-semibold text-zinc-100 leading-snug">{item.title}</h3>
          <div className="flex items-center gap-2 mt-2">
            {brand && <><span className="w-2 h-2 rounded-full" style={{ background: brand.color_hex }} /><span className="text-xs text-zinc-400">{brand.name}</span></>}
            <span className={`status-badge ml-auto ${STATUS_CLASSES[item.status] || "status-unprocessed"}`}>{STATUS_LABELS[item.status] || item.status}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">source</p><p className="text-sm text-zinc-200 capitalize">{item.source}</p></div>
          <div><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">type</p><p className="text-sm text-zinc-200">{item.type}</p></div>
          <div><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">duration</p><p className="text-sm text-zinc-200">{item.duration ? `${Math.floor(item.duration/60)}:${(item.duration%60).toString().padStart(2,"0")}` : "--"}</p></div>
          <div><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">views</p><p className="text-sm text-zinc-200">{(item.view_count || 0).toLocaleString()}</p></div>
        </div>
        {item.clip_path && (
          <div><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">processed clip</p><p className="text-xs text-teal font-mono truncate mt-1">{item.clip_path}</p></div>
        )}
        {item.status === "failed" && item.error && (
          <div className="rounded-xl px-3 py-2 bg-rose-500/10">
            <p className="text-[10px] text-rose-300/70 uppercase font-mono tracking-wider mb-1">
              why it failed{item.failed_at ? ` · ${new Date(item.failed_at).toLocaleString()}` : ""}
            </p>
            <p className="text-xs text-rose-300 leading-relaxed">{item.error}</p>
          </div>
        )}

        {/* Source privacy — unlisted/private films are client deliverables and must not be posted */}
        {privacy && (
          <div className={`rounded-xl px-3 py-2 text-xs ${isPublic ? "bg-status-green/10 text-status-green" : "bg-rose-500/10 text-rose-300"}`}>
            {isPublic
              ? `Public on ${item.source === "vimeo" ? "Vimeo" : "YouTube"} — safe to post`
              : `${privacy.toUpperCase()} on ${item.source === "vimeo" ? "Vimeo" : "YouTube"} — publishing is blocked. Client deliverables shouldn't be posted publicly.`}
          </div>
        )}

        {/* Has this film been posted, and in what form? */}
        <div>
          <p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider mb-1.5">posting status</p>
          {!posting ? (
            <p className="text-xs text-zinc-600">Loading…</p>
          ) : !posting.ever_posted && !posting.in_queue ? (
            <p className="text-xs text-zinc-400">Never posted.</p>
          ) : (
            <div className="space-y-1.5">
              {posting.ever_posted && (
                <p className="text-xs text-status-green">
                  Posted {posting.published_count}×
                  {posting.posted_as_clip && posting.posted_as_full ? " — both the AI edit and the full video"
                    : posting.posted_as_full ? " — the full video" : " — the AI-edited clip only"}
                </p>
              )}
              {posting.in_queue && <p className="text-xs text-gold">Currently in the approval queue.</p>}
              {posting.history.slice(0, 4).map((h) => (
                <div key={h.post_id} className="text-[11px] text-zinc-500 font-mono flex items-center gap-2">
                  <span className={h.status === "published" ? "text-status-green" : "text-zinc-500"}>{h.status}</span>
                  <span className="text-zinc-600">{h.post_kind === "full" ? "full video" : `clip${h.clip_duration ? " " + Math.round(h.clip_duration) + "s" : ""}`}</span>
                  <span className="text-zinc-600">{(h.platforms || []).join("+")}</span>
                  <span className="text-zinc-700 ml-auto">{(h.published_at || h.scheduled_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-2">
          {item.type === "video" && item.source !== "ai_reel" && item.source !== "video_ad" && (
            <button disabled={!isPublic}
              title={!isPublic ? "This video is not public on its source platform" : ""}
              onClick={async () => {
              try {
                const endpoint = item.status === "queued" || item.clip_path
                  ? `/content/${item.id}/auto-pipeline`
                  : `/content/${item.id}/auto-pipeline`;
                toast.info("Processing video: face detection, crop, AI segment selection, caption generation...");
                await api.post(endpoint);
                toast.success("Pipeline started. Check Approval Queue in a few minutes.");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Pipeline failed");
              }
            }}
              className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-teal/30 text-teal hover:bg-teal/10 transition-colors"
              data-testid="detail-auto-pipeline">
              <Sparkles size={15} /> Cut to Reel & Queue
            </button>
          )}
          {item.type === "video" && (
            <button
              disabled={postingFull || !isPublic}
              title={!isPublic ? "This video is not public on its source platform" : "Queue the entire video, not the AI edit"}
              onClick={async () => {
                setPostingFull(true);
                try {
                  const r = await api.post(`/content/${item.id}/post-full-video`, {});
                  toast.success("Full video queued for approval");
                  const d = await api.get(`/content/${item.id}`);
                  setDetail(d.data);
                  if (r.data?.caption) toast.info(r.data.caption);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not queue full video");
                }
                setPostingFull(false);
              }}
              className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-40"
              data-testid="detail-post-full-video">
              {postingFull ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Post Full Video (no AI edit)
            </button>
          )}
          {(item.still_urls?.length >= 3) && (
            <button
              disabled={creatingCarousel || !isPublic}
              title={!isPublic ? "This video is not public on its source platform" : `Queue a carousel of ${item.still_urls.length} AI-picked stills`}
              onClick={async () => {
                setCreatingCarousel(true);
                try {
                  const r = await api.post(`/content/${item.id}/create-carousel`);
                  toast.success(`Stills carousel queued (${r.data.frames} frames)`);
                  if (r.data?.caption) toast.info(r.data.caption);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not create carousel");
                }
                setCreatingCarousel(false);
              }}
              className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-teal/30 text-teal hover:bg-teal/10 transition-colors disabled:opacity-40"
              data-testid="detail-create-carousel">
              {creatingCarousel ? <Loader2 size={15} className="animate-spin" /> : <Images size={15} />}
              Post Stills Carousel ({item.still_urls.length} frames)
            </button>
          )}
          <button onClick={() => onCreatePost(item)}
            className="w-full btn-gold py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="detail-create-post">
            <Check size={15} /> Create Custom Post
          </button>
        </div>
      </div>
    </div>
  );
}

function DropboxBrowser({ brands, onClose }) {
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [basePath, setBasePath] = useState("");
  const [viewingFolder, setViewingFolder] = useState(null);
  const [assignBrand, setAssignBrand] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [error, setError] = useState("");

  const browse = useCallback(async (p) => {
    setLoading(true);
    setError("");
    setPhotos([]);
    setViewingFolder(null);
    try {
      const res = await api.get("/dropbox/browse", { params: { path: p || "" } });
      setEntries(res.data.entries);
      setPath(res.data.path);
      setBasePath(res.data.base_path);
      if (res.data.error) setError(res.data.error);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to browse Dropbox");
    }
    setLoading(false);
  }, []);

  useEffect(() => { browse(""); }, [browse]);

  const openFolder = async (folderPath, folderName) => {
    // Check if it's a leaf wedding folder (has photos) or a category folder
    setLoading(true);
    try {
      const res = await api.get("/dropbox/thumbnails", { params: { path: folderPath, limit: 12 } });
      if (res.data.photos.length > 0) {
        setPhotos(res.data.photos);
        setViewingFolder({ path: folderPath, name: folderName, total: res.data.total_in_folder });
      } else {
        browse(folderPath);
      }
    } catch {
      browse(folderPath);
    }
    setLoading(false);
  };

  const handleUseFolder = async () => {
    if (!viewingFolder || !assignBrand) return;
    setAssigning(true);
    try {
      const res = await api.post(`/dropbox/use-folder?folder_path=${encodeURIComponent(viewingFolder.path)}&brand_id=${assignBrand}`);
      toast.success(`Assigned ${viewingFolder.name} to brand (${res.data.photo_count} photos)`);
      setViewingFolder(null);
      setPhotos([]);
      browse(path);
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      if (e.response?.status === 409) {
        setConfirmDialog({ message: detail });
      } else {
        toast.error(detail);
      }
    }
    setAssigning(false);
  };

  const goUp = () => {
    if (viewingFolder) {
      setViewingFolder(null);
      setPhotos([]);
      return;
    }
    if (!path || path === basePath) return;
    const parent = path.split("/").slice(0, -1).join("/");
    if (parent.length >= basePath.length) browse(parent);
  };

  const weddingBrands = brands.filter((b) => ["shutter_and_sound", "rain_on_your_wedding_day"].includes(b.id));
  const currentFolder = path.split("/").pop() || "Photo Galleries";

  return (
    <div className="space-y-4" data-testid="dropbox-browser">
      {/* Header */}
      <div className="flex items-center gap-3">
        {(path !== basePath || viewingFolder) && (
          <button onClick={goUp} className="p-2 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold transition-colors" data-testid="dropbox-back">
            <ArrowLeft size={16} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-zinc-200 font-medium truncate">{viewingFolder ? viewingFolder.name : currentFolder}</p>
          <p className="text-[11px] text-zinc-500 font-mono truncate">{viewingFolder ? `${viewingFolder.total} photos` : path}</p>
        </div>
      </div>

      {error && (
        <div className="bg-gold/5 border border-gold/20 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-gold mt-0.5 flex-shrink-0" />
          <p className="text-xs text-zinc-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner-gold" /></div>
      ) : viewingFolder ? (
        /* Photo grid view */
        <div className="space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p) => (
              <div key={p.path} className="aspect-square rounded-lg overflow-hidden bg-studio-surface">
                <img src={p.link} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 p-4 bg-studio-raised border border-[#2a2e36] rounded-xl">
            <Select value={assignBrand} onValueChange={setAssignBrand}>
              <SelectTrigger className="w-[220px] bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="dropbox-brand-select">
                <SelectValue placeholder="Assign to brand..." />
              </SelectTrigger>
              <SelectContent className="bg-studio-raised border-[#2a2e36]">
                {weddingBrands.map((b) => (
                  <SelectItem key={b.id} value={b.id} className="text-zinc-300 text-sm">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: b.color_hex }} />{b.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={handleUseFolder} disabled={!assignBrand || assigning}
              className="btn-gold px-4 h-10 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-40" data-testid="dropbox-use-btn">
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Use for Brand
            </button>
          </div>
        </div>
      ) : (
        /* Folder list */
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden">
          {entries.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No folders found</p>
          ) : (
            entries.filter((e) => e.is_folder).map((entry) => (
              <div key={entry.path}
                onClick={() => openFolder(entry.path, entry.name)}
                className="flex items-center gap-3 p-3.5 border-b border-[#222630] hover:bg-[#1a1d21] transition-colors cursor-pointer group"
                data-testid={`dropbox-folder-${entry.name.replace(/\s/g, "-").toLowerCase()}`}>
                <FolderOpen size={16} className="text-zinc-500 group-hover:text-gold transition-colors flex-shrink-0" />
                <span className="text-sm text-zinc-200 flex-1 truncate">{entry.name}</span>
                {entry.used_by_brand && (
                  <span className="text-[9px] font-mono uppercase px-2 py-0.5 rounded-full bg-status-green/10 text-status-green border border-status-green/20">
                    used -- {entry.used_by_brand}
                  </span>
                )}
                <ChevronRight size={14} className="text-zinc-600 group-hover:text-zinc-400 transition-colors" />
              </div>
            ))
          )}
        </div>
      )}

      {/* Cross-brand warning dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="bg-studio-raised border-[#2a2e36] rounded-2xl max-w-md" style={{ backgroundColor: "#1a1d21" }}>
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-display flex items-center gap-2">
              <AlertTriangle size={18} className="text-status-red" /> Cross-Brand Conflict
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">{confirmDialog?.message}</p>
          <DialogFooter>
            <button onClick={() => setConfirmDialog(null)} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">OK</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ContentLibrary() {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDropbox, setShowDropbox] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genBrand, setGenBrand] = useState("");
  const [genDuration, setGenDuration] = useState("5");
  const [genAspect, setGenAspect] = useState("9:16");
  const [genImageUrl, setGenImageUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genModel, setGenModel] = useState("kling-v1-6");
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmClearFailed, setConfirmClearFailed] = useState(false);

  // Read brand from URL params (for brand card links from dashboard)
  const urlBrand = new URLSearchParams(window.location.search).get("brand") || "";
  const [filters, setFilters] = useState({ brand: urlBrand, type: "", status: "", source: "", page: 1 });

  const handleClearFailed = async () => {
    if (!confirmClearFailed) {
      setConfirmClearFailed(true);
      setTimeout(() => setConfirmClearFailed(false), 4000);
      return;
    }
    setConfirmClearFailed(false);
    try {
      const res = await api.post(`/content/clear-failed${filters.brand ? `?brand=${filters.brand}` : ""}`);
      toast.success(`Cleared ${res.data.deleted_count} failed item(s)`);
      fetchContent();
    } catch { toast.error("Could not clear failed items"); }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: 20 };
      if (filters.brand) params.brand = filters.brand;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      const [contentRes, brandsRes] = await Promise.all([
        api.get("/content", { params }),
        api.get("/brands"),
      ]);
      const brandMap = {};
      brandsRes.data.brands.forEach((b) => { brandMap[b.id] = b; });
      setBrands(brandsRes.data.brands);
      let enriched = contentRes.data.items.map((item) => ({
        ...item,
        brand_name: brandMap[item.brand_id]?.name || item.brand_id,
        brand_color: brandMap[item.brand_id]?.color_hex || "#71717a",
      }));
      if (filters.source) {
        enriched = enriched.filter((i) => i.source === filters.source);
      }
      setItems(enriched);
      setTotal(contentRes.data.total);
    } catch {
      toast.error("Failed to load content");
    }
    setLoading(false);
  };

  useEffect(() => { fetchContent(); }, [filters]);

  // Poll generating items
  useEffect(() => {
    const generatingItems = items.filter((i) => i.status === "generating" && i.source === "kling");
    if (generatingItems.length === 0) return;
    const interval = setInterval(async () => {
      for (const item of generatingItems) {
        try {
          const res = await api.get(`/kling/task/${item.source_id}`);
          if (res.data.status === "complete" || res.data.status === "failed") {
            fetchContent();
          }
        } catch { /* ignore */ }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [items]);

  const handleGenerate = async () => {
    if (!genPrompt.trim() || !genBrand) return;
    setGenerating(true);
    try {
      const endpoint = genImageUrl ? "/kling/image-to-video" : "/kling/text-to-video";
      const body = {
        prompt: genPrompt, brand_id: genBrand,
        duration: genDuration, aspect_ratio: genAspect, model_name: genModel, mode: "pro",
      };
      if (genImageUrl) body.image_url = genImageUrl;
      await api.post(endpoint, body);
      toast.success("Video generation started");
      setShowGenerate(false);
      setGenPrompt(""); setGenImageUrl("");
      fetchContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    }
    setGenerating(false);
  };

  const [showCreatePost, setShowCreatePost] = useState(false);
  const [cpBrand, setCpBrand] = useState("");
  const [cpContent, setCpContent] = useState("");
  const [cpCaption, setCpCaption] = useState("");
  const [cpPlatforms, setCpPlatforms] = useState(["instagram", "facebook"]);
  const [cpSchedule, setCpSchedule] = useState("");
  const [cpGenerating, setCpGenerating] = useState(false);
  const [cpSubmitting, setCpSubmitting] = useState(false);

  const handleCreatePost = async () => {
    if (!cpBrand) return;
    setCpSubmitting(true);
    try {
      await api.post("/posts/create", {
        brand_id: cpBrand, content_item_id: cpContent === "none" ? undefined : cpContent || undefined,
        caption: cpCaption, platforms: cpPlatforms,
        scheduled_at: cpSchedule || undefined
      });
      toast.success("Post added to approval queue");
      setShowCreatePost(false);
      setCpCaption(""); setCpContent(""); setCpSchedule("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setCpSubmitting(false);
  };

  const openCreatePostFromDetail = (item) => {
    const brand = brands.find((b) => b.id === item.brand_id);
    setCpBrand(item.brand_id);
    setCpContent(item.id);
    setCpCaption("");
    setShowCreatePost(true);
    setSelectedItem(null);
    // Auto-generate caption after dialog opens
    setTimeout(async () => {
      setCpGenerating(true);
      try {
        const res = await api.post("/ai/generate-caption", {
          brand_id: item.brand_id, video_title: item.title || "social media post",
          context: brand?.voice_profile || ""
        });
        setCpCaption(res.data.caption);
      } catch { /* silent */ }
      setCpGenerating(false);
    }, 100);
  };

  const generateCaption = async () => {
    if (!cpBrand) { toast.error("Select a brand first"); return; }
    setCpGenerating(true);
    try {
      const brand = brands.find((b) => b.id === cpBrand);
      const title = cpContent ? items.find((i) => i.id === cpContent)?.title || "" : "";
      const res = await api.post("/ai/generate-caption", {
        brand_id: cpBrand, video_title: title || "social media post",
        context: brand?.voice_profile || ""
      });
      setCpCaption(res.data.caption);
    } catch { toast.error("Caption generation failed"); }
    setCpGenerating(false);
  };

  const aiBrands = brands.filter((b) => ["pixally", "uncut_gems", "rain_on_your_wedding_day"].includes(b.id));

  const filtered = search
    ? items.filter((i) => i.title.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="content-library">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-100">Content Library</h1>
          <p className="section-label mt-2">// all media across brands</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreatePost(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm btn-gold"
            data-testid="create-post-btn"
          >
            + Create Post
          </button>
          <button
            onClick={() => setShowGenerate(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-zinc-400 border border-[#2a2e36] hover:border-[#a78bfa40] hover:text-[#a78bfa] transition-all"
            data-testid="generate-video-btn"
          >
            <Wand2 size={15} />
            Generate Video
          </button>
          <button
            onClick={() => setShowDropbox(!showDropbox)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all ${
              showDropbox ? "text-gold border border-gold/30 bg-gold/5" : "text-zinc-400 border border-[#2a2e36] hover:border-[#363b45]"
            }`}
            data-testid="toggle-dropbox-btn"
          >
          <FolderOpen size={15} />
          {showDropbox ? "Close Dropbox" : "Browse Dropbox"}
        </button>
        </div>
      </div>

      {showDropbox && (
        <DropboxBrowser brands={brands} onClose={() => setShowDropbox(false)} />
      )}

      {/* Generate Video Dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="bg-studio-raised border-[#2a2e36] rounded-2xl max-w-lg" style={{ backgroundColor: "#1a1d21" }}>
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-display flex items-center gap-2">
              <Wand2 size={18} className="text-[#a78bfa]" /> Generate Video
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="section-label block mb-2">// prompt</label>
              <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)}
                placeholder="Describe the video you want to generate..."
                className="w-full h-24 bg-studio-surface border border-[#2a2e36] rounded-xl p-3 text-sm text-zinc-200 outline-none focus:border-gold-dim resize-none placeholder:text-zinc-600"
                data-testid="gen-prompt" />
            </div>
            <div>
              <label className="section-label block mb-2">// brand</label>
              <Select value={genBrand} onValueChange={setGenBrand}>
                <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="gen-brand">
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent className="bg-studio-raised border-[#2a2e36]">
                  {aiBrands.map((b) => (
                    <SelectItem key={b.id} value={b.id} className="text-zinc-300 text-sm">
                      <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: b.color_hex }} />{b.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="section-label block mb-2">// model</label>
                <Select value={genModel} onValueChange={setGenModel}>
                  <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="gen-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-studio-raised border-[#2a2e36]">
                    {KLING_MODELS.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-zinc-300 text-sm">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="section-label block mb-2">// duration</label>
                <div className="flex bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
                  {["5", "10"].map((d) => (
                    <button key={d} onClick={() => setGenDuration(d)}
                      className={`flex-1 py-2 text-xs transition-colors ${genDuration === d ? "bg-gold/10 text-gold" : "text-zinc-500"}`}>
                      {d}s
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <label className="section-label block mb-2">// aspect ratio</label>
                <div className="flex bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
                  {["9:16", "1:1", "16:9"].map((a) => (
                    <button key={a} onClick={() => setGenAspect(a)}
                      className={`flex-1 py-2 text-xs transition-colors ${genAspect === a ? "bg-gold/10 text-gold" : "text-zinc-500"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="section-label block mb-2">// reference image (optional)</label>
              <input value={genImageUrl} onChange={(e) => setGenImageUrl(e.target.value)}
                placeholder="https://... image URL for image-to-video"
                className="w-full h-10 px-3 bg-studio-surface border border-[#2a2e36] rounded-xl text-sm text-zinc-200 outline-none focus:border-gold-dim placeholder:text-zinc-600"
                data-testid="gen-image-url" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowGenerate(false)} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">Cancel</button>
            <button onClick={handleGenerate} disabled={generating || !genPrompt.trim() || !genBrand}
              className="px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-40"
              style={{ background: "#a78bfa", color: "#0f1114" }}
              data-testid="gen-submit">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              {generating ? "Generating..." : "Generate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="bg-studio-raised border-[#2a2e36] rounded-2xl max-w-lg" style={{ backgroundColor: "#1a1d21" }}>
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-display">Create Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="section-label block mb-2">// brand</label>
              <Select value={cpBrand} onValueChange={setCpBrand}>
                <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="cp-brand">
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent className="bg-studio-raised border-[#2a2e36]">
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id} className="text-zinc-300 text-sm">
                      <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: b.color_hex }} />{b.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="section-label block mb-2">// content item (optional)</label>
              <Select value={cpContent} onValueChange={setCpContent}>
                <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="cp-content">
                  <SelectValue placeholder="Select from library..." />
                </SelectTrigger>
                <SelectContent className="bg-studio-raised border-[#2a2e36] max-h-48">
                  <SelectItem value="none" className="text-zinc-500 text-sm">No content item</SelectItem>
                  {items.filter((i) => !cpBrand || i.brand_id === cpBrand).slice(0, 20).map((i) => (
                    <SelectItem key={i.id} value={i.id} className="text-zinc-300 text-sm">{i.title?.slice(0, 50)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="section-label">// caption</label>
                <button onClick={generateCaption} disabled={cpGenerating}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-gold/30 text-gold hover:bg-gold/5 transition-colors flex items-center gap-1 disabled:opacity-40"
                  data-testid="cp-generate-caption">
                  {cpGenerating ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                  Generate with Jarvis
                </button>
              </div>
              <textarea value={cpCaption} onChange={(e) => setCpCaption(e.target.value)}
                placeholder="Write or generate a caption..."
                className="w-full h-24 bg-studio-surface border border-[#2a2e36] rounded-xl p-3 text-sm text-zinc-200 outline-none focus:border-gold-dim resize-none placeholder:text-zinc-600"
                data-testid="cp-caption" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="section-label block mb-2">// platforms</label>
                <div className="flex gap-2">
                  {["instagram", "facebook"].map((p) => (
                    <button key={p} onClick={() => setCpPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])}
                      className={`flex-1 py-2 text-xs rounded-xl border transition-colors ${cpPlatforms.includes(p) ? "bg-gold/10 text-gold border-gold/30" : "text-zinc-500 border-[#2a2e36]"}`}>
                      {p === "instagram" ? "Instagram" : "Facebook"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <label className="section-label block mb-2">// schedule (optional)</label>
                <input type="datetime-local" value={cpSchedule} onChange={(e) => setCpSchedule(e.target.value)}
                  className="w-full h-10 px-3 bg-studio-surface border border-[#2a2e36] rounded-xl text-sm text-zinc-200 outline-none focus:border-gold-dim" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowCreatePost(false)} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">Cancel</button>
            <button onClick={handleCreatePost} disabled={cpSubmitting || !cpBrand}
              className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-2 disabled:opacity-40"
              data-testid="cp-submit">
              {cpSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Add to Queue
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-studio-surface border border-[#2a2e36] rounded-xl px-3 flex-1 min-w-[200px] max-w-[400px]">
          <Search size={16} className="text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search content..."
            className="bg-transparent text-zinc-200 text-sm py-2.5 outline-none flex-1 placeholder:text-zinc-600"
            data-testid="content-search-input"
          />
        </div>

        <Select value={filters.brand} onValueChange={(v) => setFilters((p) => ({ ...p, brand: v === "all" ? "" : v, page: 1 }))}>
          <SelectTrigger className="w-[180px] bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="content-filter-brand">
            <SelectValue placeholder="All brands" />
          </SelectTrigger>
          <SelectContent className="bg-studio-raised border-[#2a2e36]">
            <SelectItem value="all" className="text-zinc-300 text-sm">All brands</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b.id} value={b.id} className="text-zinc-300 text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: b.color_hex }} />
                  {b.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Source filter */}
        <div className="flex items-center bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
          {[{ key: "", label: "All" }, { key: "vimeo", label: "Vimeo" }, { key: "youtube", label: "YT" }, { key: "dropbox", label: "Dropbox" }, { key: "kling", label: "AI Gen" }].map((s) => (
            <button key={s.key} onClick={() => setFilters((p) => ({ ...p, source: s.key, page: 1 }))}
              className={`px-3 py-2 text-xs transition-colors ${filters.source === s.key ? "bg-gold/10 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`source-filter-${s.key || "all"}`}>
              {s.label}
            </button>
          ))}
        </div>

        <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v === "all" ? "" : v, page: 1 }))}>
          <SelectTrigger className="w-[150px] bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl" data-testid="content-filter-status">
            <SelectValue placeholder="All status" />
          </SelectTrigger>
          <SelectContent className="bg-studio-raised border-[#2a2e36]">
            <SelectItem value="all" className="text-zinc-300 text-sm">All status</SelectItem>
            <SelectItem value="unprocessed" className="text-zinc-300 text-sm">Unprocessed</SelectItem>
            <SelectItem value="queued" className="text-zinc-300 text-sm">Queued</SelectItem>
            <SelectItem value="posted" className="text-zinc-300 text-sm">Posted</SelectItem>
            <SelectItem value="failed" className="text-zinc-300 text-sm">Failed</SelectItem>
          </SelectContent>
        </Select>

        <button onClick={handleClearFailed}
          className={`px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 border transition-colors ${
            confirmClearFailed
              ? "border-status-red bg-status-red/10 text-status-red"
              : "border-status-red/30 text-status-red/80 hover:bg-status-red/10"}`}
          title="Remove every failed item from the library (their failure reasons are shown on the cards)"
          data-testid="clear-failed-btn">
          <X size={12} />
          {confirmClearFailed ? "Really clear all failed?" : `Clear Failed${filters.brand ? " (this brand)" : ""}`}
        </button>

        <span className="text-xs text-zinc-500 font-mono ml-auto">{total} items</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="spinner-gold" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Film size={40} className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No content found</p>
          <p className="section-label mt-2">// sync your vimeo or youtube, or browse dropbox</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
          {filtered.map((item) => (
            <ContentCard key={item.id} item={item} onClick={setSelectedItem} />
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedItem && (
        <DetailPanel item={selectedItem} brands={brands} onClose={() => setSelectedItem(null)} onCreatePost={openCreatePostFromDetail} />
      )}
    </div>
  );
}
